import React, {PropsWithChildren} from 'react'
import {Link} from 'react-router-dom'

import {getPillTypeByMaterial} from '../../utils/utils'
import Flag from '../icons/Flag'
import Icon from '../icons/Icon'
import {CountryCode, countryMap} from '../icons/types'
import Pill from '../pills/Pill'
import {MaterialType} from './FacilityCard'

interface FacilityPreviewCardProps {
  name: string;
  facilityUrl?: string;
  countryCode?: CountryCode;
  city?: string;
  materialReused?: MaterialType;
  linkText?: string;
  imageUrl?: string;
  externalUrl?: boolean; // externalUrl === false can only be used in a Router context as it creates a <Link> component
  className?: string;
}

interface NavComponentProps {
  url: string;
  externalUrl: boolean;
  target: string;
  rel: string;
  className: string;
}

const NavComponent = ({
  url,
  externalUrl,
  ...props
}: PropsWithChildren<NavComponentProps>) => {
  return externalUrl
    ? React.createElement('a', {...props, href: url})
    : React.createElement(Link, {...props, to: url})
}

const FacilityPreviewCard = ({
  name,
  countryCode,
  materialReused,
  imageUrl,
  city,
  linkText = 'See facility page',
  facilityUrl,
  className,
  externalUrl = false
}: FacilityPreviewCardProps) => {

  return (
    <div className={`flex w-full sm:p-1.5 rounded-md bg-kwota-neutral-100 relative font-titillium gap-2 ${imageUrl ? 'text-kwota-neutral-100 sm:text-kwota-neutral-800' : 'text-kwota-neutral-800'} ${className}`}>

      {/* Image */}
      {imageUrl &&
        <div className="absolute flex-shrink-0 w-full h-full overflow-hidden rounded-md sm:h-auto sm:relative sm:w-1/3">
          <img
            className="absolute object-cover object-center w-full h-full -translate-y-1/2 rounded-md top-1/2"
            src={imageUrl}
            alt={name}
          />
          <div className="sm:hidden absolute w-full h-full bg-gradient-to-r from-kwota-neutral-800 to-kwota-neutral-800/20 to-80%" />
        </div>
      }

      {/* Body */}
      <div className="relative flex flex-col flex-shrink gap-2 p-3 basis-2/3 sm:p-0">
        {city && countryCode ?
          <div>
            <div className="flex items-center justify-between gap-2">
              <h3
                className="text-xl font-semibold leading-0 line-clamp-1 max-w-[20ch]"
                title={name}
              >
                {name}
              </h3>
              {materialReused &&
                <Pill
                  text={materialReused}
                  size="sm"
                  textTransform="capitalize-first"
                  type={getPillTypeByMaterial(materialReused)}
                />
              }
            </div>
            <div className="flex items-center gap-1">
              <Flag
                countryCode={countryCode}
                shape="rounded-square"
                size="sm"
                className={`${imageUrl ? 'sm:ring-[1px] sm:ring-kwota-neutral-400' : 'ring-[1px] ring-kwota-neutral-400'}`}
              />
              <p>{city},</p>
              <p>{countryMap[countryCode]}</p>
            </div>
          </div>
          :
          <div>
            <div className="flex items-center gap-2">
              <h3
                className="text-xl font-semibold leading-0 line-clamp-1"
                title={name}
              >
                {name}
              </h3>
              {countryCode &&
                <Flag
                  countryCode={countryCode}
                  shape="rounded-square"
                  size="sm"
                  className={`flex-shrink-0 ${imageUrl ? 'sm:ring-[1px] sm:ring-kwota-neutral-400' : 'ring-[1px] ring-kwota-neutral-400'}`}
                />
              }
            </div>
            {materialReused &&
              <Pill
                text={materialReused}
                size="sm"
                textTransform="capitalize-first"
                type={getPillTypeByMaterial(materialReused)}
              />
            }
          </div>
        }
        {facilityUrl &&
          <NavComponent
            url={facilityUrl}
            externalUrl={externalUrl}
            className="self-start inline-block mt-auto font-semibold rounded-sm focus:outline-none focus:ring-2 focus:ring-kwota-primary focus:ring-offset-1"
            target="_blank"
            rel="noreferrer noopener"
          >
            <div className="flex items-center">
              {linkText}
              <Icon
                size="sm"
                icon="open"
                className={`ml-1 ${imageUrl ? 'fill-kwota-neutral-100 sm:fill-kwota-neutral-800' : 'sm:fill-kwota-neutral-800'}`}
              />
            </div>
          </NavComponent>
        }
      </div>
    </div>
  )
}

export default FacilityPreviewCard
